/**
 * AgencyPortalPage.jsx
 * ------------------------------------------------------------------
 * Standalone landing for the Agency mode at `/agency`.
 *
 * Tabs:
 *   1. Overview   — multi-client KPI cards + recent batches
 *   2. Clients    — CRUD table + CSV import (drag-mapper)
 *   3. Bulk Run   — multi-select clients → fire bulk scan / bulk GEO
 *   4. Batches    — history of dispatched batches with polling
 *
 * Feature-gated: if the user doesn't have `agency_mode=true` in their
 * resolved limits, the API returns 402 and we render an upgrade CTA
 * pointing at `/pricing#addon_agency_portal`.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../config";
import { useAuth } from "../contexts/AuthContext";
import {
  Building2, Users, FileBarChart, ListChecks, Loader2, ArrowRight,
  Lock, TrendingUp, Palette, Plug, Heart, AlertTriangle, Minus,
} from "lucide-react";
import AgencyClientsTab from "../components/agency/AgencyClientsTab";
import AgencyBulkRunTab from "../components/agency/AgencyBulkRunTab";
import AgencyBatchesTab from "../components/agency/AgencyBatchesTab";
import AgencyBrandingTab from "../components/agency/AgencyBrandingTab";
import AgencyIntegrationsTab from "../components/agency/AgencyIntegrationsTab";
import { AgencyAgentFeed } from "../components/agency/AgencyAgentFeed";

const TABS = [
  { id: "overview",     label: "Prehľad",     icon: TrendingUp },
  { id: "clients",      label: "Klienti",     icon: Users },
  { id: "bulkrun",      label: "Bulk Run",    icon: ListChecks },
  { id: "batches",      label: "Job log",     icon: FileBarChart },
  { id: "branding",     label: "Branding",    icon: Palette },
  { id: "integrations", label: "Integrácie",  icon: Plug },
];

export default function AgencyPortalPage() {
  const { user } = useAuth();
  const userEmail = user?.email || "";
  const [activeTab, setActiveTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [error, setError] = useState(null);

  const loadOverview = useCallback(async () => {
    // Unauthenticated landing must not hang on the loader — render
    // the upgrade gate so the visitor sees the CTA + login link.
    if (!userEmail) {
      setLocked(true);
      setLoading(false);
      return;
    }
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${API}/agency/overview`, {
        headers: { "X-User-Email": userEmail },
      });
      setOverview(r.data);
      setLocked(false);
    } catch (e) {
      if (e?.response?.status === 402) setLocked(true);
      else setError(e?.response?.data?.detail || "Načítanie zlyhalo");
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  // Upgrade gate — user is paid but doesn't have agency mode addon.
  if (locked) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
        <div className="max-w-md text-center" data-testid="agency-upgrade-gate">
          <Lock className="w-10 h-10 text-purple-400 mx-auto mb-4" />
          <h1 className="text-3xl font-black mb-3">Agency Portal je odomykateľný</h1>
          <p className="text-gray-400 mb-6">
            Vyberte si <span className="text-purple-300 font-bold">Agency Partner balík (od €99/mes)</span> a získate
            zoznam klientov s AI agentom, schvaľovanie krokov, CSV import, hromadné skeny a reporty pod vlastnou značkou.
          </p>
          <a
            href="/pre-agentury#balicky"
            data-testid="agency-upgrade-cta"
            className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-500 text-white font-black uppercase tracking-wider rounded-sm"
          >
            Pozrieť balíky <ArrowRight className="w-4 h-4" />
          </a>
          {!userEmail && (
            <a
              href="/login?redirect=/agency"
              data-testid="agency-login-link"
              className="block mt-4 text-sm text-gray-400 hover:text-purple-300 underline"
            >
              Máš účet? Prihlás sa →
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="agency-portal-page">
      <header className="bg-black/80 backdrop-blur-xl border-b border-gray-800 px-6 py-4 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-purple-600 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-black tracking-tight">TopRankPulse · Agency</span>
          </a>
          <nav className="flex items-center gap-4 text-xs uppercase tracking-widest text-gray-400">
            <a href="/dashboard" className="hover:text-white">Dashboard</a>
            <a href="/eshop-scanner" className="hover:text-white">Scanner</a>
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Tab nav */}
        <nav className="flex flex-wrap gap-2 mb-8 border-b border-gray-800 pb-2" data-testid="agency-tabs">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                data-testid={`agency-tab-${t.id}`}
                className={`px-4 py-2 text-sm font-bold rounded-sm flex items-center gap-2 transition-colors ${
                  activeTab === t.id
                    ? "bg-purple-600 text-white"
                    : "text-gray-400 hover:text-white hover:bg-gray-900"
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </nav>

        {error && (
          <div className="p-3 bg-rose-900/20 border border-rose-700/40 rounded-sm text-sm text-rose-200 mb-6">
            {String(error)}
          </div>
        )}

        {loading && !overview && (
          <div className="flex items-center gap-2 text-gray-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Načítam…
          </div>
        )}

        {activeTab === "overview" && overview && (
          <section className="space-y-6" data-testid="agency-overview">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <KpiCard label="Klienti" value={overview.client_count} suffix={`/ ${overview.client_limit}`} testid="agency-kpi-clients" />
              <KpiCard label="Skenov spolu" value={overview.scan_count} testid="agency-kpi-scans" />
              <KpiCard label="GEO stránok" value={overview.pages_count} testid="agency-kpi-pages" />
              <KpiCard label="Bežiace batche" value={overview.running_batches} testid="agency-kpi-running" />
            </div>

            <div className="p-6 bg-gradient-to-br from-purple-900/30 to-black border border-purple-700/40 rounded-sm">
              <div className="text-xs uppercase tracking-widest text-purple-400 mb-1">Quickstart</div>
              <h2 className="text-2xl font-black mb-2">3 kroky k prvému bulk auditu</h2>
              <ol className="space-y-2 text-sm text-gray-300 list-decimal list-inside">
                <li>Pridaj klientov manuálne alebo importuj CSV (tab <b>Klienti</b>).</li>
                <li>V tabe <b>Bulk Run</b> označ klientov a spusti scan + GEO pipeline.</li>
                <li>Stiahni 1-page PDF report z karty každého klienta.</li>
              </ol>
            </div>

            <AgencyAgentFeed userEmail={userEmail} />

            {overview.nps && (
              <ClientNpsDashboard nps={overview.nps} onJumpToClients={() => setActiveTab("clients")} />
            )}
          </section>
        )}

        {activeTab === "clients" && (
          <AgencyClientsTab userEmail={userEmail} onClientChanged={loadOverview} />
        )}

        {activeTab === "bulkrun" && (
          <AgencyBulkRunTab userEmail={userEmail} onBatchStarted={loadOverview} />
        )}

        {activeTab === "batches" && (
          <AgencyBatchesTab userEmail={userEmail} />
        )}

        {activeTab === "branding" && (
          <AgencyBrandingTab userEmail={userEmail} />
        )}

        {activeTab === "integrations" && (
          <AgencyIntegrationsTab userEmail={userEmail} />
        )}
      </main>
    </div>
  );
}

function KpiCard({ label, value, suffix, testid }) {
  return (
    <div className="p-5 bg-gray-900/40 border border-gray-800 rounded-sm" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">{label}</div>
      <div className="text-3xl font-black tabular-nums">
        {value?.toLocaleString?.("sk-SK") ?? value}
        {suffix && <span className="text-base text-gray-500 ml-2">{suffix}</span>}
      </div>
    </div>
  );
}

/**
 * ClientNpsDashboard
 * ------------------------------------------------------------------
 * Two side-by-side cards built off `/api/agency/overview → nps`:
 *   • Top 5 spokojní klienti     — high approval %, agency keeps them
 *   • Top 5 ktorí potrebujú pozornosť — low approval / silence, churn risk
 *
 * Also surfaces a portfolio-wide summary strip on top so the agency
 * has a "1 number" health check before drilling into individual rows.
 * The "Pošli pripomienky" button manually triggers the weekly nudger
 * for clients with ≥5 pending pages older than 7 days (idempotent per
 * ISO week).
 */
function ClientNpsDashboard({ nps, onJumpToClients }) {
  const { happy = [], attention = [], summary = {} } = nps || {};
  const noActivity = (happy.length === 0 && attention.length === 0)
    || ((summary.clients_with_activity || 0) === 0 && (summary.total_pending || 0) === 0);
  const [nudging, setNudging] = useState(false);
  const [nudgeResult, setNudgeResult] = useState(null);
  const { user } = useAuth();

  const runNudge = async () => {
    if (!user?.email) return;
    setNudging(true); setNudgeResult(null);
    try {
      const r = await axios.post(`${API}/agency/nudge-pending`, {}, {
        headers: { "X-User-Email": user.email },
      });
      setNudgeResult(r.data?.stats || {});
    } catch (e) {
      setNudgeResult({ error: e?.response?.data?.detail || "Nudge failed" });
    } finally {
      setNudging(false);
    }
  };

  const hasNudgeCandidates = (summary.total_pending || 0) > 0;

  return (
    <section data-testid="agency-nps-dashboard" className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-purple-400 mb-1">
            Client Health · posledných {nps?.window_days || 30} dní
          </div>
          <h2 className="text-2xl font-black">Klientske NPS skóre</h2>
        </div>
        <div className="flex gap-3 text-xs text-gray-400 items-center" data-testid="agency-nps-summary">
          <SummaryPill label="Priem. schválenie" value={`${summary.avg_approval_rate ?? 0}%`} accent="emerald" />
          <SummaryPill label="Schválené" value={summary.total_approved ?? 0} accent="emerald" />
          <SummaryPill label="Zamietnuté" value={summary.total_rejected ?? 0} accent="rose" />
          <SummaryPill label="Čaká" value={summary.total_pending ?? 0} accent="amber" />
          {hasNudgeCandidates && (
            <button
              type="button"
              onClick={runNudge}
              disabled={nudging}
              data-testid="agency-nps-nudge-btn"
              className="px-3 py-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white text-xs font-black uppercase tracking-wider rounded-sm flex items-center gap-1"
            >
              {nudging
                ? <><Loader2 className="w-3 h-3 animate-spin" /> Posielam…</>
                : <>Pošli pripomienky</>}
            </button>
          )}
        </div>
      </div>

      {nudgeResult && (
        <div
          className={`p-3 text-xs rounded-sm border ${
            nudgeResult.error
              ? "bg-rose-900/20 border-rose-700/40 text-rose-200"
              : "bg-emerald-900/20 border-emerald-700/40 text-emerald-200"
          }`}
          data-testid="agency-nps-nudge-result"
        >
          {nudgeResult.error
            ? `Chyba: ${String(nudgeResult.error)}`
            : `Pripomenuté ${nudgeResult.clients_nudged ?? 0} klientom · `
              + `${nudgeResult.client_emails_sent ?? 0} emailov klientom, `
              + `${nudgeResult.agency_emails_sent ?? 0} FYI · `
              + `preskočené: ${(nudgeResult.skipped_under_threshold ?? 0)
                  + (nudgeResult.skipped_already_nudged ?? 0)
                  + (nudgeResult.skipped_no_contact ?? 0)}`}
        </div>
      )}

      {noActivity ? (
        <div
          className="p-8 bg-gray-900/40 border border-dashed border-gray-700 rounded-sm text-center text-sm text-gray-400"
          data-testid="agency-nps-empty"
        >
          <Heart className="w-6 h-6 text-gray-600 mx-auto mb-3" />
          Žiadna schvaľovacia aktivita za posledných 30 dní. Pošli klientom share linky
          a uvidíš ich health skóre tu.
          <div className="mt-4">
            <button
              type="button"
              onClick={onJumpToClients}
              data-testid="agency-nps-cta-clients"
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white text-xs font-black uppercase tracking-wider rounded-sm"
            >
              Otvoriť klientov <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <NpsListCard
            testid="agency-nps-happy"
            icon={<Heart className="w-5 h-5 text-emerald-400" />}
            title="Top 5 spokojných klientov"
            subtitle="Vysoká miera schválenia za posledných 30 dní"
            tone="emerald"
            rows={happy}
            emptyHint="Zatiaľ žiadny klient nemá schválené 50 %+ stránok."
          />
          <NpsListCard
            testid="agency-nps-attention"
            icon={<AlertTriangle className="w-5 h-5 text-amber-400" />}
            title="Top 5 ktorí potrebujú pozornosť"
            subtitle="Nízka miera schválenia alebo veľa nevybavených stránok"
            tone="amber"
            rows={attention}
            emptyHint="Všetci tvoji klienti sú aktuálne v poriadku — dobrá práca! 🎉"
          />
        </div>
      )}
    </section>
  );
}

function SummaryPill({ label, value, accent }) {
  const colors = {
    emerald: "text-emerald-300 border-emerald-700/40",
    rose:    "text-rose-300 border-rose-700/40",
    amber:   "text-amber-300 border-amber-700/40",
  }[accent] || "text-gray-300 border-gray-700/40";
  return (
    <div className={`px-3 py-2 bg-black/40 border rounded-sm ${colors}`}>
      <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className="text-sm font-black tabular-nums">{value}</div>
    </div>
  );
}

function NpsListCard({ icon, title, subtitle, tone, rows, emptyHint, testid }) {
  const borderTone = tone === "emerald"
    ? "border-emerald-900/40 bg-gradient-to-br from-emerald-950/30 to-black"
    : "border-amber-900/40 bg-gradient-to-br from-amber-950/30 to-black";
  return (
    <div className={`p-5 border rounded-sm ${borderTone}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <h3 className="text-base font-black">{title}</h3>
      </div>
      <p className="text-xs text-gray-400 mb-4">{subtitle}</p>
      {rows.length === 0 ? (
        <div className="py-6 text-center text-xs text-gray-500" data-testid={`${testid}-empty`}>
          <Minus className="w-4 h-4 mx-auto mb-2 text-gray-700" />
          {emptyHint}
        </div>
      ) : (
        <ul className="space-y-2" data-testid={`${testid}-list`}>
          {rows.map((r, idx) => (
            <NpsRow key={r.client_id} rank={idx + 1} row={r} tone={tone} testid={`${testid}-row-${r.client_id}`} />
          ))}
        </ul>
      )}
    </div>
  );
}

function NpsRow({ rank, row, tone, testid }) {
  // approval_rate band — green ≥ 80, yellow 50–79, orange 1–49, gray 0
  let rateColor = "text-gray-500";
  if (row.actioned > 0) {
    if (row.approval_rate >= 80) rateColor = "text-emerald-300";
    else if (row.approval_rate >= 50) rateColor = "text-yellow-300";
    else rateColor = "text-orange-300";
  }
  const barColor = tone === "emerald" ? "bg-emerald-500/70" : "bg-amber-500/70";
  // For attention rows with 0 actions but pending pages, show pending bar.
  const barPct = row.actioned > 0
    ? row.approval_rate
    : (row.pending > 0 ? Math.min(100, row.pending * 10) : 0);
  return (
    <li
      className="flex items-center gap-3 p-3 bg-black/40 border border-gray-800 rounded-sm hover:bg-black/60 transition-colors"
      data-testid={testid}
    >
      <span className="text-xs text-gray-500 font-black tabular-nums w-5 text-right">#{rank}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <div className="font-bold text-sm truncate" data-testid={`${testid}-name`}>
            {row.name}
          </div>
          <div className={`text-sm font-black tabular-nums ${rateColor}`} data-testid={`${testid}-rate`}>
            {row.actioned > 0 ? `${row.approval_rate}%` : "—"}
          </div>
        </div>
        <div className="text-[11px] text-gray-500 truncate">{row.domain}</div>
        <div className="mt-1.5 h-1 bg-gray-900 rounded-sm overflow-hidden">
          <div
            className={`h-full ${barColor} transition-all`}
            style={{ width: `${barPct}%` }}
          />
        </div>
        <div className="mt-1 flex gap-3 text-[10px] uppercase tracking-widest text-gray-500">
          <span data-testid={`${testid}-approved`}>✓ {row.approved}</span>
          <span data-testid={`${testid}-rejected`}>✗ {row.rejected}</span>
          <span data-testid={`${testid}-pending`}>⏳ {row.pending}</span>
        </div>
      </div>
    </li>
  );
}
